/**
 * A venue's address, written the same way everywhere (D-150).
 *
 * The columns have been on `venues` since the first migration, and the parts
 * were being assembled by hand in the places that bothered — the iCal feed, a
 * Stripe Terminal location, the Google Wallet pass — each with its own idea of
 * what to do when a line is missing. The ticket, the confirmation email and My
 * Tickets printed the venue's name and nothing else, which is not an address
 * and is no help at the door.
 *
 * One formatter, so a customer reads the same address on the page they bought
 * from, in the email, on the PDF and in their wallet.
 */

export interface VenueAddressParts {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
}

const clean = (value: string | null | undefined): string => (typeof value === 'string' ? value.trim() : '');

/**
 * The address as it is printed, one entry per line, never including the name.
 * A venue with nothing filled in returns an empty array rather than a line of
 * stray punctuation — which is the whole reason this is not four template
 * strings in four files.
 */
export function venueAddressLines(venue: VenueAddressParts): string[] {
  const lines = [clean(venue.address_line1), clean(venue.address_line2)];
  // "Burlington, IA 52601", and correct when any of the three is missing:
  // "Burlington, IA", "IA 52601", "Burlington".
  const city = clean(venue.city);
  const region = clean(venue.region);
  const postal = clean(venue.postal_code);
  const locality = [city, [region, postal].filter(Boolean).join(' ')].filter(Boolean).join(city && (region || postal) ? ', ' : '');
  return [...lines, locality].filter(Boolean);
}

/** The same on one line, for a ticket face or a single line of a receipt. */
export function venueAddressOneLine(venue: VenueAddressParts): string {
  return venueAddressLines(venue).join(', ');
}

/**
 * Name and address together, for the one place a caller has room for a single
 * string — a calendar location, a map link, a wallet pass.
 */
export function venueWithAddress(name: string, venue: VenueAddressParts): string {
  const address = venueAddressOneLine(venue);
  return address ? `${name}, ${address}` : name;
}

/**
 * What the compact blocks show: the venue and the town it is in, and never the
 * street. A hero panel and a listing row are telling somebody which town to
 * drive to, not how to find the door.
 */
export function venueAndCity(name: string, venue: VenueAddressParts): string {
  const city = clean(venue.city);
  const region = clean(venue.region);
  const where = [city, region].filter(Boolean).join(', ');
  return where ? `${name} · ${where}` : name;
}
